import * as React from 'react';

import {
  DirectionalHint,
  doesElementContainFocus,
  EventGroup,
  getDocument,
  getId,
  IKeytipProps,
  IOverflowSet,
  IOverflowSetItemProps,
  ITheme,
  ktpTargetFromId,
  mergeOverflows,
  OverflowSet,
  sequencesToID,
} from '@fluentui/react';

import { AppFlyoutAnchor } from '@1js/acui-button';
import { dataUniqueId, UILocation, WithKey } from '@1js/acui-common';
import { AppContext, AppContextType } from '@1js/acui-context';
import { MenuDefinition, MenuSection, SimpleMenuItemControlProps } from '@1js/acui-menu';
import { AppThemeConsumer } from '@1js/acui-theme';

import { AppControlProps } from '../../../controls';
import { RibbonFlyoutAnchor } from '../../../Ribbon';
import { RibbonControlInternalProps } from '../../Controls/RibbonControlProps';
import { hasPinnedCombobox } from '../../utilities/hasPinnedControl';
import { shouldShadowRenderMenu } from '../RibbonControlGroup';
import { getRibbonGroupItemsIcon } from '../RibbonControlGroup/getRibbonGroupItemsIcon';
import { RibbonControlGroupBase } from '../RibbonControlGroup/RibbonControlGroup';
import { RibbonEnlightenedComponent } from '../RibbonEnlightenedComponent';
import { RibbonControlGroupSLRProps } from './RibbonControlGroupSLR.Props';
import {
  getOverflowLauncherStyles,
  getOverflowSetItemStyles,
  getRibbonControlGroupSLRClassNames,
} from './RibbonControlGroupSLR.styles';

export class RibbonControlGroupSLR
  extends React.PureComponent<RibbonControlGroupSLRProps, {}>
  implements RibbonControlGroupBase {
  private overflowSet = React.createRef<IOverflowSet>();
  private overflowButtonLegacy = React.createRef<AppFlyoutAnchor>();
  private overflowButton = React.createRef<HTMLButtonElement>();
  private container = React.createRef<HTMLDivElement>();
  private events: EventGroup;
  private overflowGroupButtonId: string;

  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public constructor(props: RibbonControlGroupSLRProps) {
    super(props);
    this.events = new EventGroup(this);
    this.overflowGroupButtonId = getId(props.id);
  }

  private setKeyAndKeytipsForOverflowControls(
    overflowControls: SimpleMenuItemControlProps[],
    menuSectionTitle: string = ''
  ): IOverflowSetItemProps[] {
    const { keytipProps } = this.props;

    return overflowControls.map((item: any) => {
      const extendedItem = { ...item, key: item.id, menuSectionTitle };
      if (keytipProps && item.keytipProps) {
        const itemKeytipProps = { ...item.keytipProps };
        // Set the keytip onExecute function here, need to do this so the persisted
        // keytips can be triggered before we've mapped the items to props
        // which is done in RibbonFlyoutAnchor internally
        switch (item.type) {
          case 'AppSpinButtonProps':
          case 'AppComboBoxProps':
            // Override persisted input items (spin and combo box) onExecute manually.
            itemKeytipProps.onExecute = () => {
              this.inputInMenuExecute(itemKeytipProps.keySequences, keytipProps.keySequences);
            };
            break;
          default:
            itemKeytipProps.onExecute = () => {
              item.onExecute({});
            };
            break;
        }
        extendedItem.keytipProps = itemKeytipProps;
      }
      return extendedItem;
    });
  }

  public componentDidMount(): void {
    if (this.container.current && this.props.onFocusIn) {
      this.events.on(this.container.current, 'focusin', this.props.onFocusIn, true /* useCapture */);
    }
  }

  public componentWillUnmount(): void {
    this.events.off();
  }

  public render() {
    const classNames = getRibbonControlGroupSLRClassNames();
    const { overflowControls, multiMenuOverflowControls, keytipProps, fastDisabled } = this.props;

    let overflowItems: IOverflowSetItemProps[] = [];
    if (multiMenuOverflowControls && multiMenuOverflowControls.length !== 0) {
      // Because Overflow Set doesn't support complex menus with sub-headers, we will
      // simply flatten our structured MenuSections with an extra parameter and
      // regroup the subsections in the rendering phase
      overflowItems = multiMenuOverflowControls.reduce((items, overflowMenuSection) => {
        return [
          ...items,
          ...this.setKeyAndKeytipsForOverflowControls(overflowMenuSection.controls, overflowMenuSection.title),
        ];
      }, []);
    } else if (overflowControls && overflowControls.length !== 0) {
      overflowItems = this.setKeyAndKeytipsForOverflowControls(overflowControls, this.props.overflowMenuLauncherLabel);
    }

    return (
      <div className={`${classNames.root}`} ref={this.container}>
        <OverflowSet
          componentRef={this.overflowSet}
          role={'group'}
          aria-label={this.props.groupLabel}
          items={this.getContextualTopLevelControlsWithKey(fastDisabled)}
          overflowItems={overflowItems}
          onRenderItem={this.onRenderItem}
          onRenderOverflowButton={this.onRenderOverflowButton}
          keytipSequences={keytipProps && keytipProps.keySequences}
          itemSubMenuProvider={this.itemSubMenuProvider}
          data-unique-id={dataUniqueId(this.props)}
          styles={getOverflowSetItemStyles()}
        />
      </div>
    );
  }

  private getContextualTopLevelControlsWithKey = (fastDisabled?: boolean | (() => boolean | undefined)) => {
    const topLevelControlsWithKey =
      this.props.topLevelControls &&
      this.props.topLevelControls.map((item, index, controls) => {
        return {
          ...item,
          icon: getRibbonGroupItemsIcon(item),
          key: item.id,
          fastDisabledCallback: typeof fastDisabled === 'function' ? fastDisabled : undefined,
          shadowRenderMenu: shouldShadowRenderMenu(
            `shouldShadowRenderFn-${item.id}${item.location || ''}-SLR`,
            typeof this.props.fastDisabled === 'function' ? this.props.fastDisabled?.() : undefined,
            (item as any).shadowRenderMenu
          ),
          ...hasPinnedCombobox(controls, index),
        };
      });
    return topLevelControlsWithKey;
  };

  public focus() {
    if (this.overflowSet.current) {
      this.overflowSet.current.focus(true /* forceIntoFirstChild */);
    }
  }

  public hasContainer(): boolean {
    return !!this.container.current;
  }

  public containsFocus(): boolean {
    if (this.hasContainer()) {
      return doesElementContainFocus(this.container.current as HTMLElement);
    }

    return false;
  }

  private onRenderOverflowButton = (overflowItems: any[]) => {
    const content: JSX.Element = (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <RibbonFlyoutAnchor
            ref={this.overflowButton}
            legacyRef={this.overflowButtonLegacy}
            type="AppFlyoutAnchorProps"
            id={this.overflowGroupButtonId}
            variant="overflowButton"
            icon={this.props.overflowMenuLauncherIcon}
            menuDefinition={
              this.props.deferredOverflowMenuAs ? undefined : constructMenuDefinitionForOverflow(overflowItems)
            }
            directionalHint={DirectionalHint.bottomLeftEdge}
            label={this.props.overflowMenuLauncherLabel}
            disabled={this.props.overflowMenuLauncherDisabled}
            hideLabel={true}
            hideChevron={true}
            styles={getOverflowLauncherStyles(theme)}
            onExecute={this.props.onOverflowMenuOpen}
            location={UILocation.Overflow}
            onAfterMenuDismiss={this.props.onOverflowMenuClose}
            keytipProps={this.props.keytipProps}
            controlInMenuRendererFunction={this.props.controlInMenuRendererFunction}
            downChevronIcon={this.props.overflowMenuLauncherIcon}
            deferredMenuDefinition={
              this.props.deferredOverflowMenuAs
                ? {
                    type: 'DeferredOverflowMenuDefinition',
                    menuAs: this.props.deferredOverflowMenuAs,
                    overflowMenuDefinition: constructMenuDefinitionForOverflow(overflowItems),
                    parentKeytipSequence: this.props.keytipProps?.keySequences,
                  }
                : undefined
            }
            preventMenuExecution={this.props.preventMenuExecution}
          />
        )}
      </AppThemeConsumer>
    );

    return this.props.isRibbonSpanning ? (
      <RibbonEnlightenedComponent
        id={this.props.overflowMenuLauncherLabel}
        key={this.props.overflowMenuLauncherLabel}
        offset={this.props.spanningComponentId === this.props.overflowMenuLauncherLabel ? this.props.spanningOffset : 0}
      >
        {content}
      </RibbonEnlightenedComponent>
    ) : (
      content
    );
  };

  private onRenderItem = (controlProps: WithKey<AppControlProps>) => {
    const content: JSX.Element = this.props.controlRendererFunction({
      ...controlProps,
      animate: this.props.animate,
    } as WithKey<RibbonControlInternalProps>);

    return this.props.isRibbonSpanning ? (
      <RibbonEnlightenedComponent
        id={controlProps.key}
        key={controlProps.key}
        offset={this.props.spanningComponentId === controlProps.key ? this.props.spanningOffset : 0}
      >
        {content}
      </RibbonEnlightenedComponent>
    ) : (
      content
    );
  };

  /**
   * itemSubMenuProvider needed for keytips integration
   * The items we pass to our OverflowSet have not yet been converted to props (RibbonFlyoutAnchor does this internally)
   * This lets the OverflowSet know if an item has a submenu or not
   */
  private itemSubMenuProvider = (item: IOverflowSetItemProps): any[] | boolean | undefined => {
    const menuDefinition: MenuDefinition = (item as any).menuDefinition;
    const deferredMenuDefinition = (item as any).deferredMenuDefinition;
    const keytipProps: IKeytipProps | undefined = (item as any).keytipProps;
    return !!menuDefinition || !!deferredMenuDefinition || (keytipProps && !!keytipProps.hasMenu);
  };

  /**
   * Persisted keytip for input items callback.
   * Should open the overflow menu and focus the relevant item.
   * We need to override the default focusing in BaseButton openMenu so we can focus the item we want.
   */
  private inputInMenuExecute = (keySequences: string[], overflowKeySequences: string[]) => {
    if (this.overflowButtonLegacy && this.overflowButtonLegacy.current) {
      this.overflowButtonLegacy.current.focus();
      this.overflowButtonLegacy.current.openMenu(false, false);
      // Find and focus the relevant input element manually
      // Won't be passed as an onExecute param because it doesn't exist until the menu is opened
      const seqWithOverflow = mergeOverflows(keySequences, overflowKeySequences);
      const inputEl = getDocument()!.querySelector(ktpTargetFromId(sequencesToID(seqWithOverflow))) as HTMLElement;
      inputEl && inputEl.focus();
    }
  };
}

function constructMenuDefinitionForOverflow(overflowItems: any[]): MenuDefinition | undefined {
  const menuSectionsByKey: {
    [s: string]: AppControlProps[];
  } = {};
  overflowItems.forEach(({ menuSectionTitle, ...overflowItem }) => {
    menuSectionsByKey[menuSectionTitle] = menuSectionsByKey[menuSectionTitle] || [];
    menuSectionsByKey[menuSectionTitle].push({ ...overflowItem, icon: getRibbonGroupItemsIcon(overflowItem) });
  });
  const menuSections: MenuSection[] = Object.keys(menuSectionsByKey).map(sectionTitle => ({
    title: sectionTitle,
    controls: menuSectionsByKey[sectionTitle],
  }));

  return menuSections.length === 0
    ? undefined
    : {
        sections: menuSections,
      };
}
